import styles from './styles.module.css'

import { RxMagicWand } from '@react-icons/all-files/rx/RxMagicWand'
import { RxPlus } from '@react-icons/all-files/rx/RxPlus'
import React from 'react'

import {
  Badge,
  Button as DsButton,
  Card,
  Flex,
  Heading,
  Link,
  Separator,
  Text,
  Tooltip,
} from 'components/ds'

type Props = {
  title: string
  description: React.ReactNode
  learnMoreUrl: string
  ctaHref?: string
  ctaLabel?: string
  ctaDisabledReason?: string
}

/**
 * Reusable card for add-ons promoted from the manage-subscription page.
 */
const SubscriptionAddOnCard = ({
  title,
  description,
  learnMoreUrl,
  ctaHref,
  ctaLabel = 'Add to plan',
  ctaDisabledReason,
}: Props): React.ReactElement => {
  const isBlocked = Boolean(ctaDisabledReason)
  const isLoading = !isBlocked && !ctaHref
  const isLink = !isBlocked && Boolean(ctaHref)
  const content = (
    <>
      <RxPlus aria-hidden="true" className={styles.plusIcon} />
      {ctaLabel}
    </>
  )

  const button = (
    <DsButton
      asChild={isLink}
      aria-label={`${ctaLabel}, ${title}`}
      className={styles.cta}
      color="gray"
      data-testid="subscription-addon-cta"
      highContrast
      size="2"
      loading={isLoading}
      // Explicit, because passing false would override Radix's disabled-while-loading default
      disabled={isLoading}
      // aria-disabled instead of disabled: a natively disabled button swallows the pointer events its tooltip needs
      aria-disabled={isBlocked || undefined}
      data-disabled={isBlocked || undefined}
    >
      {isLink ? (
        <a href={ctaHref} target="_blank" rel="noopener noreferrer">
          {content}
        </a>
      ) : (
        content
      )}
    </DsButton>
  )

  const cta = ctaDisabledReason ? (
    <Tooltip content={ctaDisabledReason}>{button}</Tooltip>
  ) : (
    button
  )

  return (
    <Card asChild className={styles.card} variant="surface">
      <article>
        <Flex direction="column" align="start" gap="2" width="100%">
          <Flex align="start" className={styles.titleRow}>
            <Heading as="h3" size="2" weight="medium">
              {title}
            </Heading>
            <Badge color="green" radius="small" size="1" variant="soft">
              <RxMagicWand aria-hidden="true" className={styles.badgeIcon} />
              Add on
            </Badge>
          </Flex>
          <Text as="p" color="gray" size="1">
            {description}
          </Text>
        </Flex>

        <Separator size="4" />

        <Flex
          align="start"
          className={styles.actions}
          justify="between"
          width="100%"
        >
          <Link asChild color="gray" size="2" underline="hover">
            <a
              className={styles.learnMore}
              href={learnMoreUrl}
              aria-label={`Learn more about ${title}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Learn more
            </a>
          </Link>

          {cta}
        </Flex>
      </article>
    </Card>
  )
}

export default SubscriptionAddOnCard
